/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from 'react'

// react-router-dom components
import { Link, useLocation,
  // useNavigate
 } from 'react-router-dom'

// @mui material components
import Card from '@mui/material/Card'
// import Switch from '@mui/material/Switch'
import Grid from '@mui/material/Grid'
// import MuiLink from '@mui/material/Link'

// // @mui icons
// import FacebookIcon from '@mui/icons-material/Facebook'
// import GitHubIcon from '@mui/icons-material/GitHub'
// import GoogleIcon from '@mui/icons-material/Google'

// Material Dashboard 2 React components
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import MDInput from 'components/MDInput'
import MDButton from 'components/MDButton'

// Authentication layout components
import BasicLayout from 'layouts/authentication/components/BasicLayout'

// Images
import bgImage from 'assets/images/bg-sign-in-basic.jpeg'
import { useFormik } from 'formik'
import { AuthVld } from 'validation'
import { useAppServices } from 'hook/services'
import { useUserInfo } from 'context/user'
import localforage from 'localforage'
import { useBrandInfo } from 'context/brand'
import { useAgencyInfo } from 'context/agency'
import { useOtpAction } from 'context/otpAction'
import OtpVerifyModal from 'components/OtpVerifyModal'
// import { Button } from '@mui/material'

function Basic() {
  // const [rememberMe, setRememberMe] = useState(false)

  // const handleSetRememberMe = () => setRememberMe(!rememberMe)
  const Service      = useAppServices()
  const updateBrand  = useBrandInfo()[1]
  const updateAgency = useAgencyInfo()[1]
  const updateUser   = useUserInfo()[1]
  const { markVerified } = useOtpAction()
  // const navigate     = useNavigate()
  const { state }    = useLocation()

  const [processing, setProcessing] = useState(false)
  const [message, setMessage]       = useState()
  const [expiredNotice, setExpiredNotice] = useState(false)

  // If we landed here because the OTP-verified session timed out, show a
  // short notice once and clear the flag so a manual refresh later doesn't
  // keep showing it.
  useEffect(() => {
    try {
      if (window.sessionStorage.getItem('aloca_session_expired') === '1') {
        setExpiredNotice(true)
        window.sessionStorage.removeItem('aloca_session_expired')
      }
    } catch (e) {
      // sessionStorage may be blocked — silent fallback, no notice.
    }
  }, [])

  // 2nd-step state — once /login returns requiresOtp, we open the modal
  // and hold the email here so /login/verifyOtp knows whose OTP to check.
  const [otpOpen, setOtpOpen]           = useState(false)
  const [otpEmail, setOtpEmail]         = useState('')
  const [otpExpiresAt, setOtpExpiresAt] = useState(null)
  const [otpBusy, setOtpBusy]           = useState(false)
  const [otpError, setOtpError]         = useState('')

  const initState = {
    email    : '',
    password : '',
  }

  // After OTP succeeds we run the same persistence + context update the
  // original handler did. Kept in one place so step 1 can't accidentally
  // diverge from step 2.
  const finalizeLogin = async (response) => {
    const { token, ...user } = response.data
    await localforage.setItem('token', token)
    await localforage.setItem('AdminUser', user)
    if (response.brand) await localforage.setItem('brand', response.brand)
    if (response.agency) await localforage.setItem('agency', response.agency)

    if (response.brand) updateBrand({ ...response.brand })
    if (response.agency) updateAgency({ ...response.agency })
    updateUser(response.data)

    // Start the 2-hour OTP-verified session so the next 2 hours of
    // mutating calls don't re-prompt.
    await markVerified()
  }

  const handleSubmit = async (form) => {
    setProcessing(true)
    setMessage('')
    const { response, error } = await Service.auth.login({
      payload: { ...form, location_id: state?.location_id },
      // Login itself bypasses the OTP gate (we'd loop forever otherwise)
      // and we haven't got a session token yet anyway.
      skipOtp: true,
    })
    setProcessing(false)
    if (error) return setMessage(error.message)

    // Step 1 always returns requiresOtp now. Open the OTP modal.
    if (response?.data?.requiresOtp) {
      setOtpEmail(response.data.email || form.email)
      setOtpExpiresAt(response.data.otpExpiresAt || null)
      setOtpError('')
      setOtpOpen(true)
      return ''
    }

    // Defensive fallback — in case an older backend still returns the
    // single-step token directly, keep working.
    if (response?.data?.token) {
      await finalizeLogin(response)
    }
    return ''
  }

  const handleVerifyOtp = async (otp) => {
    setOtpBusy(true)
    setOtpError('')
    const { response, error } = await Service.auth.loginVerifyOtp({
      payload: { email: otpEmail, otp },
      skipOtp: true,
    })
    setOtpBusy(false)
    if (error) {
      setOtpError(error.message || 'Invalid OTP.')
      return
    }
    await finalizeLogin(response)
    setOtpOpen(false)
  }

  const handleResendOtp = async () => {
    if (!otpEmail) return
    setOtpBusy(true)
    setOtpError('')
    // Re-running step 1 issues a fresh OTP for the same admin.
    const { response, error } = await Service.auth.login({
      payload: { email: otpEmail, password: formik.values.password },
      skipOtp: true,
    })
    setOtpBusy(false)
    if (error) {
      setOtpError(error.message || 'Could not resend OTP.')
      return
    }
    setOtpExpiresAt(response?.data?.otpExpiresAt || null)
  }

  const formik = useFormik({
    initialValues    : { ...initState },
    onSubmit         : handleSubmit,
    validationSchema : AuthVld.Login,
  })

  return (
    <BasicLayout image={bgImage}>
        <Card>
          <MDBox
            variant       = "gradient"
            bgColor       = "info"
            borderRadius  = "lg"
            coloredShadow = "info"
            mx            = {2}
            mt            = {-3}
            p             = {2}
            mb            = {1}
            textAlign     = "center"
          >
            <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
              Sign in
            </MDTypography>
            <Grid container spacing={3} justifyContent="center" sx={{ mt: 1, mb: 2 }}>

              {/* <Grid item xs={2}>
                <MDTypography component={MuiLink} href="#" variant="body1" color="white">
                  <FacebookIcon color="inherit" />
                </MDTypography>
              </Grid> */}
              {/* <Grid item xs={2}>
                <MDTypography component={MuiLink} href="#" variant="body1" color="white">
                  <GitHubIcon color="inherit" />
                </MDTypography>
              </Grid> */}
              {/* <Grid item xs={2}>
                <MDTypography component={MuiLink} href="#" variant="body1" color="white">
                  <GoogleIcon color="inherit" />
                </MDTypography>
              </Grid> */}
            </Grid>
          </MDBox>
          <MDBox pt={4} pb={3} px={3}>
            {expiredNotice ? (
              <MDBox
                mb={2}
                px={2}
                py={1.5}
                sx={{
                  borderRadius: 1,
                  bgcolor: '#FFF4E5',
                  border: '1px solid #F5C16C',
                }}
              >
                <MDTypography variant="caption" color="warning" fontWeight="medium" display="block">
                  Your session expired
                </MDTypography>
               
              </MDBox>
            ) : null}
            <MDBox component="form" onSubmit={formik.handleSubmit} role="form">
              <MDBox mb={2}>
                <MDInput
                  type       = "email"
                  label      = "Email"
                  name       = "email"
                  onChange   = {formik.handleChange}
                  inputProps = {{onFocus: formik.handleBlur}}
                  value      = {formik.values.email}
                  error      = {formik.touched.email && formik.errors.email}
                  helperText = {formik.touched.email && formik.errors.email ? formik.errors.email : ''}
                  success    = {formik.touched.email && !formik.errors.email}
                  fullWidth 
                />
              </MDBox>
              <MDBox mb={2}>
                <MDInput 
                  type       = "password"
                  label      = "Password"
                  name       = "password"
                  onChange   = {formik.handleChange}
                  inputProps = {{onFocus: formik.handleBlur}}
                  value      = {formik.values.password}
                  error      = {formik.touched.password && formik.errors.password}
                  helperText = {formik.touched.password && formik.errors.password ? formik.errors.password : ''}
                  success    = {formik.touched.password && !formik.errors.password}
                  fullWidth 
                />
              </MDBox>
              {/* <MDBox display="flex" alignItems="center" ml={-1}>
                <Switch checked={rememberMe} onChange={handleSetRememberMe} />
                <MDTypography
                  variant="button"
                  fontWeight="regular"
                  color="text"
                  onClick={handleSetRememberMe}
                  sx={{ cursor: 'pointer', userSelect: 'none', ml: -1 }}
                >
                  &nbsp;&nbsp;Remember me
                </MDTypography>
              </MDBox> */}
              <MDBox>
                <MDButton 
                  variant  = "gradient"
                  color    = "info"
                  type     = "submit"
                  loading  = {processing}
                  disabled = {processing || !formik.isValid}
                  sx       = {{ mt: 4, mb: 1 }}
                  fullWidth
                >
                  sign in
                </MDButton>
                <MDTypography variant="button" color="error">
                  {message}
                </MDTypography>
              </MDBox>
              <MDBox mt={3} mb={1} textAlign="center">
                <MDTypography variant="button" color="text">
                  Don&apos;t have an account?{' '}
                  <MDTypography
                    component={Link}
                    to="/auth/sign-up"
                    variant="button"
                    color="info"
                    fontWeight="medium"
                    textGradient
                  >
                    Sign up
                  </MDTypography>
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
        </Card>

        <OtpVerifyModal
          open={otpOpen}
          email={otpEmail}
          title="Verify your login"
          description="We sent a 6-digit code to your email. Enter it below to finish signing in."
          busy={otpBusy}
          error={otpError}
          expiresAt={otpExpiresAt}
          onVerify={handleVerifyOtp}
          onResend={handleResendOtp}
          onClose={() => {
            if (otpBusy) return
            setOtpOpen(false)
            setOtpError('')
            setMessage('')
          }}
        />
    </BasicLayout>
  )
}

export default Basic
